/** IXD 편집기 - 파일 업로드 뷰 */

import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GitCompare, Loader2 } from "lucide-react";

import { NewChip } from "@/components/common/new-chip";
import { IxdReportChips } from "@/components/common/ixd-report-name";
import { ExistingIxdReportPicker } from "@/components/existing-ixd-report-picker";
import { FileDropZone, SelectedFileCard } from "@/components/file-drop-zone";
import { IxdUploadNotice } from "@/components/ixd-upload-notice";
import { LeftSidebar } from "@/components/app-shell";
import { Button } from "@/toggle-ui-kit/button";
import {
  ViewSwitchGroup,
  type ViewSwitchOption,
} from "@/toggle-ui-kit/view-switch-group";
import { resolveBetaFeatureOptions } from "@/components/beta-feature";
import {
  uploadDartFilingPreview,
  uploadIxdPreview,
} from "@/apis/ixd-editor/route";
import { isInvalidIxdStructureError } from "@/apis/errors";
import type { IxdReportSummary } from "@/apis/ixd-editor/types";
import { UPLOAD_CARD_BY_MODE } from "@/constants/ixd-upload-mode";
import { logUserAction } from "@/apis/logs/route";
import { UploadFailedModal } from "@/components/modals/upload-failed-modal";
import { pushModal } from "@/stores/modal";
import {
  logIxdEditorAction,
  logIxdSubmissionUploadRejected,
} from "@/utils/usage-log";
import queries from "@/queries";
import {
  openPendingTab,
  resolvePendingTab,
  warnPopupBlocked,
} from "@/utils/pending-tab";
import {
  buildEditorViewUrl,
  buildFilingViewUrl,
} from "@/utils/ixd-editor/view-url";

/**
 * `dart-filing` 은 DART 공시 XBRL(zip) — 값 편집 경로가 없어 읽기 전용으로 열린다.
 * 대사 화면이 보여주지 못하는 미매핑 표를 훑는 용도라 편집 업로드와 섞지 않는다.
 */
type IxdEditorMode = "upload" | "existing-report" | "dart-filing";

/** 화면마다 뜻이 다른 설명만 여기서 정한다. label/accept 는 공유 상수. */
const CARD_DESCRIPTION_BY_MODE = {
  upload: "*.ixd 형식",
  "dart-filing": "DART 공시 XBRL(.zip) — 읽기 전용",
} as const;

/** 홈에서 "기존 IXD 선택" 탭으로 바로 들어오는 진입 파라미터. */
const PARAM_MODE = "mode";

/** 업로드 카드 머리줄 탭. Figma 7145-404819 는 2개 — 공시본은 베타일 때만 붙는다. */
const MODE_OPTIONS: (ViewSwitchOption<IxdEditorMode> & {
  isBetaFeature?: boolean;
})[] = [
  { value: "upload", label: "새 파일 업로드" },
  { value: "existing-report", label: "이전 파일 불러오기" },
  { value: "dart-filing", label: "XBRL 공시본 열람", isBetaFeature: true },
];

const MODE_TAB_ACTION = {
  upload: "새 파일 업로드 탭",
  "existing-report": "이전 파일 불러오기 탭",
  "dart-filing": "XBRL 공시본 열람 탭",
} as const satisfies Record<IxdEditorMode, string>;

/** Figma `file_uploadZone` — 두 탭이 같은 틀을 쓴다(드롭 영역 / 이전 파일 목록). */
const UPLOAD_CARD_CLASS = "h-[450px]";

export const EditorUploadView = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [ixdFile, setIxdFile] = useState<File | null>(null);
  // 홈의 "이전 IXD 불러오기"처럼 특정 탭으로 바로 보내는 진입점만 지원한다(진입 시 1회).
  // 이후 탭 전환은 URL 에 쓰지 않는다 — 복원할 다른 상태(선택한 파일)가 없다.
  const [mode, setMode] = useState<IxdEditorMode>(() =>
    searchParams.get(PARAM_MODE) === "existing-report"
      ? "existing-report"
      : "upload",
  );
  // 피커가 고른 항목을 통째로 넘겨준다 — 파일명·기간 chip 을 쓰려고 목록을 다시 읽지 않는다.
  const [selectedReport, setSelectedReport] = useState<IxdReportSummary | null>(
    null,
  );
  const isExistingReportMode = mode === "existing-report";
  const pendingPreviewTabRef = useRef<Window | null>(null);

  const openPreparedTab = (viewUrl: string) => {
    const pendingPreviewTab = pendingPreviewTabRef.current;
    pendingPreviewTabRef.current = null;
    // 업로드는 이미 끝났다 — 탭을 못 열면 같은 탭 이동이라도 줘야 조용히 묻히지 않는다
    if (!resolvePendingTab(pendingPreviewTab, viewUrl)) {
      warnPopupBlocked("화면 열기", () => navigate(viewUrl));
    }
  };

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!ixdFile) throw new Error("파일을 선택해주세요.");
      if (mode === "dart-filing") {
        const { filingId } = await uploadDartFilingPreview(ixdFile);
        return {
          viewUrl: buildFilingViewUrl(filingId, ixdFile.name),
          reportId: null,
        };
      }
      const { reportId } = await uploadIxdPreview(ixdFile);
      return { viewUrl: buildEditorViewUrl(reportId), reportId };
    },
    onSuccess: async (data) => {
      logUserAction(
        data.reportId ? "IXD 값 편집 시작" : "공시 XBRL 열람 시작",
        [
          {
            filename: ixdFile?.name ?? "",
            ...(data.reportId ? { ixdReportId: data.reportId } : {}),
          },
        ],
      );
      // 공시본은 리포트를 만들지 않으므로 목록이 바뀌지 않는다.
      if (data.reportId) {
        await queryClient.invalidateQueries({
          queryKey: queries.ixdEditorReports().queryKey,
        });
      }
      openPreparedTab(data.viewUrl);
    },
    onError: (error) => {
      pendingPreviewTabRef.current?.close();
      pendingPreviewTabRef.current = null;
      // 변환 안내 모달은 "제출용 .ixd 를 편집용으로" 라는 문구라, zip 을 올린
      // 공시본 사용자에게는 고칠 방법을 알려주지 못한다. 그쪽은 인라인 문구로 둔다.
      if (mode !== "dart-filing" && isInvalidIxdStructureError(error)) {
        logIxdSubmissionUploadRejected("IXD 값 편집기");
        pushModal(UploadFailedModal);
      }
    },
  });

  /** 모달로 이미 안내한 오류는 인라인 문구로 중복 노출하지 않는다. */
  const isHandledByModal =
    mode !== "dart-filing" && isInvalidIxdStructureError(previewMutation.error);

  const handlePreviewClick = () => {
    const what = mode === "dart-filing" ? "공시본 열람" : "IXD 편집";
    pendingPreviewTabRef.current = openPendingTab({
      title: `${what} 화면 준비 중...`,
      message: `${what} 화면을 준비하고 있습니다...`,
    });
    previewMutation.mutate();
  };

  const handleOpenSelectedReport = () => {
    if (!selectedReport) return;

    logUserAction("IXD 값 편집 시작", [
      {
        filename: selectedReport.originalFilename ?? "",
        ixdReportId: selectedReport.reportId,
      },
    ]);
    window.open(
      buildEditorViewUrl(selectedReport.reportId),
      "_blank",
      "noopener",
    );
  };

  /** 파일을 직접 올리는 모드의 계약. 목록 탭은 올릴 파일이 없어 `upload` 기준을 쓴다. */
  const uploadCard =
    UPLOAD_CARD_BY_MODE[isExistingReportMode ? "upload" : mode];

  const cardHeader = (
    <div className="flex shrink-0 items-center gap-2">
      <p className="min-w-0 flex-1 body-2-normal-semibold text-label-normal">
        IXD 업로드
      </p>
      <ViewSwitchGroup<IxdEditorMode>
        size="32px"
        className="shrink-0 p-1"
        options={resolveBetaFeatureOptions(MODE_OPTIONS)}
        value={mode}
        onChange={(next) => {
          logIxdEditorAction(MODE_TAB_ACTION[next]);
          setMode(next);
        }}
      />
    </div>
  );

  return (
    <div className="flex h-full w-full bg-white">
      <LeftSidebar activeMenu="ixd-editor" />

      <main className="flex min-w-0 flex-1 flex-col items-center overflow-y-auto bg-white px-16 pb-[100px] pt-28">
        <div className="flex w-full max-w-[1000px] flex-col gap-10">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h1 className="title-2-bold text-label-normal">IXD 값 편집기</h1>
              <NewChip color="indigo" size="large" />
            </div>
            <p className="label-1-normal-regular text-label-alternative">
              IXD 보고서를 업로드하여 값을 수정하세요.
            </p>
          </div>

          <section className="flex flex-col gap-6">
            <div className="flex flex-col items-end gap-4">
              {isExistingReportMode ? (
                <div
                  className={`flex w-full flex-col gap-2.5 rounded-lg border-2 border-dashed border-line-normal p-4 ${UPLOAD_CARD_CLASS}`}
                >
                  {cardHeader}
                  <ExistingIxdReportPicker
                    title=""
                    description=""
                    selectedReportId={selectedReport?.reportId ?? null}
                    onSelect={(_reportId, report) => setSelectedReport(report)}
                    showSelectionIndicator
                    showMetadata={false}
                    className="min-h-0 rounded-none border-0 p-0"
                    selectedTone="blue"
                    listHeight="fill"
                  />
                  {selectedReport && (
                    <div className="flex shrink-0 flex-col gap-2">
                      <div className="h-px w-full bg-line-normal" />
                      <SelectedFileCard
                        format="xbrl"
                        onRemove={() => setSelectedReport(null)}
                      >
                        <IxdReportChips
                          periodYear={selectedReport.periodYear}
                          periodType={selectedReport.periodType}
                        />
                        <p className="label-1-normal-regular truncate text-label-normal">
                          {selectedReport.originalFilename}
                        </p>
                      </SelectedFileCard>
                    </div>
                  )}
                </div>
              ) : (
                <FileDropZone
                  accept={uploadCard.accept}
                  hint={
                    CARD_DESCRIPTION_BY_MODE[
                      mode === "dart-filing" ? "dart-filing" : "upload"
                    ]
                  }
                  header={cardHeader}
                  className={UPLOAD_CARD_CLASS}
                  file={ixdFile}
                  onFileChange={setIxdFile}
                />
              )}

              {/* 업로드 오류는 업로드 탭에서만 — 탭을 옮기면 지난 오류 줄을 끌고 가지 않는다 */}
              {!isExistingReportMode &&
                previewMutation.isError &&
                !isHandledByModal && (
                  <p
                    role="alert"
                    className="w-full label-2-regular text-status-error"
                  >
                    {previewMutation.error instanceof Error
                      ? previewMutation.error.message
                      : "편집 화면을 준비하는 중 오류가 발생했습니다."}
                  </p>
                )}

              <Button
                variant="solid"
                color="blue"
                size={40}
                leftIcon={
                  previewMutation.isPending ? (
                    <Loader2 className="animate-spin" strokeWidth={1.8} />
                  ) : (
                    <GitCompare strokeWidth={1.8} />
                  )
                }
                disabled={
                  isExistingReportMode
                    ? !selectedReport
                    : !ixdFile || previewMutation.isPending
                }
                onClick={
                  isExistingReportMode
                    ? handleOpenSelectedReport
                    : handlePreviewClick
                }
              >
                {previewMutation.isPending
                  ? "준비 중..."
                  : mode === "dart-filing"
                    ? "열람 시작"
                    : "편집 시작"}
              </Button>
            </div>

            <IxdUploadNotice
              accepts={uploadCard.accept}
              showConversionGuide={uploadCard.showConversionGuide}
            />
          </section>
        </div>
      </main>
    </div>
  );
};
